import { useMemo, useState } from 'react';

import {
  appliancesOperations,
  cookingAppliancesOperations,
  cutoutOperations,
} from '../undoStack.constants';
import { ApplianceOperationType } from '../undoStack.types';

import { UserDesignOperation } from '~/core/enums';
import { DrawingShape } from '~/core/models';
import { useAppSelector } from '~/redux/hooks';
import {
  selectLastUserSelectedDesignOperation,
  selectUserDesignOperationsData,
} from '~/redux/slices/undoStack';

export const useUndoStack = () => {
  const [shapesStack, setShapesStack] = useState<DrawingShape[][]>([]);

  const {
    latestPushedOperation: latestPushedUserDesignOperation,
    latestBeforeUndoRedo: latestOperationBeforeUndoRedo,
    selectedOperations,
  } = useAppSelector(selectUserDesignOperationsData);
  const lastUserSelectedDesignOperation = useAppSelector(
    selectLastUserSelectedDesignOperation
  );

  const latestOperationIsUndoOrRedo = useMemo(
    () =>
      lastUserSelectedDesignOperation?.startsWith(UserDesignOperation.UNDO) ||
      lastUserSelectedDesignOperation?.startsWith(UserDesignOperation.REDO),
    [lastUserSelectedDesignOperation]
  );

  const checkIfIsApplianceOperation = (operation: string) =>
    appliancesOperations.some((op) => operation.startsWith(op));

  const latestOperationBeforeUndoRedoWasAddingAppliance:
    | ApplianceOperationType
    | undefined = useMemo(() => {
    if (
      !latestOperationBeforeUndoRedo ||
      (latestOperationBeforeUndoRedo &&
        !checkIfIsApplianceOperation(latestOperationBeforeUndoRedo))
    ) {
      return undefined;
    }

    return cutoutOperations.some((op) =>
      latestOperationBeforeUndoRedo.startsWith(op)
    )
      ? 'cutout'
      : cookingAppliancesOperations.some((op) =>
            latestOperationBeforeUndoRedo.startsWith(op)
          )
        ? 'cookingAppliance'
        : 'sink';
  }, [latestOperationBeforeUndoRedo]);

  const secondToLatestOperationWasAddingAppliance = useMemo(() => {
    if (!latestOperationBeforeUndoRedo) {
      return false;
    }
    const latestOperationIndex = selectedOperations.findIndex(
      (op) => op === latestOperationBeforeUndoRedo
    );
    const latestOperation = selectedOperations[latestOperationIndex];

    if (
      !checkIfIsApplianceOperation(latestOperation) ||
      latestOperationIndex === 0
    ) {
      return false;
    }

    const secondToLatestOperation =
      selectedOperations[latestOperationIndex - 1];

    return checkIfIsApplianceOperation(secondToLatestOperation);
  }, [latestOperationBeforeUndoRedo, selectedOperations]);

  return {
    shapesStack,
    setShapesStack,
    lastUserSelectedDesignOperation,
    latestOperationIsUndoOrRedo,
    latestOperationBeforeUndoRedoWasAddingAppliance,
    latestPushedUserDesignOperation,
    secondToLatestOperationWasAddingAppliance,
  };
};
